/* eslint-disable react/jsx-props-no-spreading */
import { faArrowDownShortWide, faArrowDownWideShort, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ClickAwayListener, Paper, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import useConfirm from '../../hooks/useConfirm';
import TextField from '../forms/TextField';

export interface ColumnItemProps {
  column: { id: string; label: string };
  onRemove: Function;
  listeners?: any;
  sort: boolean;
  ascending: boolean;
}

enum Sorting {
  None,
  Ascending,
  Descending,
}

function ColumnItem({ column, listeners, onRemove, sort, ascending }: ColumnItemProps) {
  const [isDeleteHovered, setDeleteHovered] = useState(false);
  const setValueOptions = {
    shouldDirty: true,
    shouldValidate: true,
  };
  const form = useFormContext();
  const { watch, getValues, setValue } = form;
  const [isRename, setIsRename] = useState(false);
  const columnLabelId = `columnLabels.${column.id}`;

  const renamedLabel = watch(columnLabelId);
  const defaultSorting = (sort && ascending && Sorting.Ascending) || (sort && Sorting.Descending) || Sorting.None;
  const [sorting, setSorting] = useState<Sorting>(defaultSorting);

  useEffect(() => {
    const newSorting = (sort && ascending && Sorting.Ascending) || (sort && Sorting.Descending) || Sorting.None;
    setSorting(newSorting);
  }, [sort, ascending]);

  const onSort = () => {
    const newSortingValue = (sorting + 1) % 3;
    setSorting(newSortingValue);
    const sortBy = getValues('sortBy') || [];
    const sortAscending = getValues('sortAscending') || [];
    if (newSortingValue === Sorting.None) {
      const indexOfColumnId = sortBy.indexOf(column.id);
      sortBy.splice(indexOfColumnId, 1);
      sortAscending.splice(indexOfColumnId, 1);
      setValue('sortBy', sortBy, setValueOptions);
      setValue('sortAscending', sortAscending, setValueOptions);
    } else if (newSortingValue === Sorting.Ascending) {
      setValue('sortBy', [...sortBy, column.id], setValueOptions);
      setValue('sortAscending', [...sortAscending, true], setValueOptions);
    } else if (newSortingValue === Sorting.Descending) {
      const indexOfColumnId = sortBy.indexOf(column.id);
      sortAscending[indexOfColumnId] = false;
      setValue('sortAscending', sortAscending, setValueOptions);
    }
  };

  let sortColor = '#8C8C8C';
  let sortIcon = faArrowDownShortWide;
  if (sorting !== Sorting.None) {
    sortColor = inMarketTheme.palette.primary.main;
  }
  if (sorting === Sorting.Descending) {
    sortIcon = faArrowDownWideShort;
  }

  const originalLabel = column.label;
  const label = renamedLabel || originalLabel;

  const toggleIsRename = () => {
    if (!isRename && !renamedLabel) {
      setValue(columnLabelId, originalLabel);
    } else if (isRename && renamedLabel === originalLabel) {
      setValue(columnLabelId, null);
    }
    setIsRename(!isRename);
  };

  const { windowDotConfirm } = useConfirm(`Are you sure you want to remove "${label}"?`);

  return (
    <Paper>
      <Stack p={1} direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Stack {...listeners}>
            <DragIndicatorIcon sx={{ cursor: 'pointer' }} color="action" />
          </Stack>
          {!isRename && <Typography>{label}</Typography>}
          {isRename && (
            <ClickAwayListener onClickAway={toggleIsRename}>
              <div>
                <TextField label="Name" name={columnLabelId} size="small" />
              </div>
            </ClickAwayListener>
          )}
        </Stack>
        <Stack direction="row" pt="5px" spacing={1}>
          <Stack sx={{ cursor: 'pointer' }} onClick={toggleIsRename} aria-label={`Rename Column ${label}`}>
            <Tooltip title="Rename Column">
              <FontAwesomeIcon icon={faPen} color="#8C8C8C" fontSize="16px" />
            </Tooltip>
          </Stack>
          <Tooltip title={`Current Sort Order: ${Sorting[sorting]}`}>
            <FontAwesomeIcon
              style={{ cursor: 'pointer' }}
              icon={sortIcon}
              color={sortColor}
              onClick={onSort}
              aria-label={`Sort Column ${label}`}
              fontSize="16px"
            />
          </Tooltip>
          <Tooltip title="Delete Column">
            <FontAwesomeIcon
              icon={faTrash}
              style={{ cursor: 'pointer' }}
              onClick={async (e) => {
                e.stopPropagation();
                if (onRemove) {
                  if (await windowDotConfirm()) {
                    onRemove(column.id);
                  }
                }
              }}
              aria-label={`Delete Column ${label}`}
              color={isDeleteHovered ? inMarketTheme.palette.error.main : '#8C8C8C'}
              onMouseOver={() => setDeleteHovered(true)}
              onMouseOut={() => setDeleteHovered(false)}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default ColumnItem;
